"use strict";
// Evaluation of special forms
Object.defineProperty(exports, "__esModule", { value: true });
exports.evaluateSpecialForm = void 0;
const errors = require("../../errors/errors");
const sExpression_1 = require("../../sExpression");
const interpreter_1 = require("../interpreter");
const procedure_1 = require("../procedure");
const quote_1 = require("../quote");
const util_1 = require("../util");
function* evaluateSpecialForm(form, context) {
    switch (form.tag) {
        case 'define':
            return yield* evaluateDefineForm(form, context);
        case 'lambda':
            return yield* evaluateLambdaForm(form, context);
        case 'set!':
            return yield* evaluateSetBangForm(form, context);
        case 'if':
            return yield* evaluateIfForm(form, context);
        case 'cond':
            return yield* evaluateCondForm(form, context);
        case 'begin':
            return yield* evaluateBeginForm(form, context);
        case 'let':
            return yield* evaluateLetForm(form, context);
        case 'let*':
            return yield* evaluateLetStarForm(form, context);
        case 'letrec':
            return yield* evaluateLetRecForm(form, context);
        case 'quote':
            return quote_1.quoteExpression(form.expression, context);
        case 'quasiquote':
            return yield* quote_1.quasiquoteExpression(form.expression, context);
        case 'unquote':
        case 'unquote-splicing':
            return util_1.handleRuntimeError(context, new errors.UnquoteInWrongContext(form.expression));
        case 'and':
            return yield* evaluateAndForm(form, context);
        case 'or':
            return yield* evaluateOrForm(form, context);
        case 'defmacro':
            return yield* evaluateDefMacroForm(form, context);
    }
}
exports.evaluateSpecialForm = evaluateSpecialForm;
function* evaluateDefineForm(defineForm, context) {
    // TODO: disallow mixing of definitions and expressions?
    let value;
    if (defineForm.variant === 'basic') {
        value = yield* interpreter_1.evaluate(defineForm.value, context);
    }
    else {
        value = {
            type: 'procedure',
            callSignature: defineForm.callSignature,
            name: defineForm.name.value,
            variant: 'CompoundProcedure',
            body: defineForm.body,
            environment: context.runtime.environments[0]
        };
    }
    const frame = context.runtime.environments[0].head;
    util_1.introduceBinding(context, frame, defineForm.name.isFromSource, defineForm.name.value, value);
    return sExpression_1.makeEmptyList();
}
function* evaluateLambdaForm(lambdaForm, context) {
    return {
        type: 'procedure',
        callSignature: lambdaForm.callSignature,
        name: '[anonymous procedure]',
        variant: 'CompoundProcedure',
        body: lambdaForm.body,
        environment: context.runtime.environments[0]
    };
}
function* evaluateSetBangForm(setBangForm, context) {
    const value = yield* interpreter_1.evaluate(setBangForm.value, context);
    util_1.setVariable(context, setBangForm.name.value, value);
    return sExpression_1.makeEmptyList();
}
function* evaluateIfForm(ifForm, context) {
    const testValue = yield* interpreter_1.evaluate(ifForm.test, context);
    procedure_1.tryEnterTailContext(context);
    if (util_1.isTruthy(testValue)) {
        return yield* interpreter_1.evaluate(ifForm.consequent, context);
    }
    else if (ifForm.alternative) {
        return yield* interpreter_1.evaluate(ifForm.alternative, context);
    }
    else {
        return sExpression_1.makeEmptyList();
    }
}
function* evaluateCondForm(condForm, context) {
    for (const clause of condForm.clauses) {
        const testResult = yield* interpreter_1.evaluate(clause.test, context);
        if (!util_1.isTruthy(testResult)) {
            continue;
        }
        if (clause.type === 'basic') {
            return yield* interpreter_1.evaluateSequence(clause.body, context, true, testResult);
        }
        else {
            const procedure = yield* interpreter_1.evaluate(clause.body, context);
            if (procedure.type !== 'procedure') {
                return util_1.handleRuntimeError(context, new errors.CondProcedureClauseError(context.runtime.nodes[0]));
            }
            if (procedure_1.isParentInTailContext(context)) {
                return {
                    type: 'TailCall',
                    procedure,
                    args: [testResult],
                    node: clause.node
                };
            }
            else {
                return yield* procedure_1.apply(context, procedure, [testResult], clause.node);
            }
        }
    }
    // none of the clauses were evaluated, evaluate the else clause
    if (!condForm.elseClause) {
        return sExpression_1.makeEmptyList();
    }
    return yield* interpreter_1.evaluateSequence(condForm.elseClause.body, context, true);
}
function* evaluateLetForm(letForm, context) {
    const frame = {};
    for (const binding of letForm.bindings) {
        const value = yield* interpreter_1.evaluate(binding.value, context);
        util_1.introduceBinding(context, frame, binding.name.isFromSource, binding.name.value, value);
    }
    const newEnvironment = util_1.extendCurrentEnvironment(context, 'letEnvironment', frame);
    util_1.pushEnvironment(context, newEnvironment);
    const result = yield* interpreter_1.evaluateSequence(letForm.body, context, true);
    util_1.popEnvironment(context);
    return result;
}
function* evaluateLetStarForm(letStarForm, context) {
    let numNewFrames = 0;
    for (const binding of letStarForm.bindings) {
        const frame = {};
        const value = yield* interpreter_1.evaluate(binding.value, context);
        util_1.introduceBinding(context, frame, binding.name.isFromSource, binding.name.value, value);
        const newEnvironment = util_1.extendCurrentEnvironment(context, 'let*Environment', frame);
        util_1.pushEnvironment(context, newEnvironment);
        numNewFrames++;
    }
    const result = yield* interpreter_1.evaluateSequence(letStarForm.body, context, true);
    while (numNewFrames--) {
        util_1.popEnvironment(context);
    }
    return result;
}
function* evaluateLetRecForm(letRecForm, context) {
    const frame = {};
    for (const binding of letRecForm.bindings) {
        util_1.introduceBinding(context, frame, binding.name.isFromSource, binding.name.value, sExpression_1.makeEmptyList());
    }
    const newEnvironment = util_1.extendCurrentEnvironment(context, 'letrecEnvironment', frame);
    util_1.pushEnvironment(context, newEnvironment);
    for (const binding of letRecForm.bindings) {
        frame[binding.name.value] = yield* interpreter_1.evaluate(binding.value, context);
    }
    const result = yield* interpreter_1.evaluateSequence(letRecForm.body, context, true);
    util_1.popEnvironment(context);
    return result;
}
function* evaluateBeginForm(beginForm, context) {
    return yield* interpreter_1.evaluateSequence(beginForm.body, context, true);
}
function* evaluateAndForm(andForm, context) {
    if (andForm.arguments.length === 0) {
        return sExpression_1.makeBool(true);
    }
    else {
        for (let i = 0; i < andForm.arguments.length - 1; i++) {
            const arg = andForm.arguments[i];
            const result = yield* interpreter_1.evaluate(arg, context);
            if (!util_1.isTruthy(result)) {
                return sExpression_1.makeBool(false);
            }
        }
        // Evaluate the last form in tail context
        const lastArg = andForm.arguments[andForm.arguments.length - 1];
        procedure_1.tryEnterTailContext(context);
        return yield* interpreter_1.evaluate(lastArg, context);
    }
}
function* evaluateOrForm(orForm, context) {
    if (orForm.arguments.length === 0) {
        return sExpression_1.makeBool(false);
    }
    else {
        for (let i = 0; i < orForm.arguments.length - 1; i++) {
            const arg = orForm.arguments[i];
            const result = yield* interpreter_1.evaluate(arg, context);
            if (util_1.isTruthy(result)) {
                return result;
            }
        }
        // Evaluate the last form in tail context
        const lastArg = orForm.arguments[orForm.arguments.length - 1];
        procedure_1.tryEnterTailContext(context);
        return yield* interpreter_1.evaluate(lastArg, context);
    }
}
function* evaluateDefMacroForm(defMacroForm, context) {
    const macro = {
        type: 'macro',
        name: defMacroForm.name.value,
        environment: context.runtime.environments[0],
        body: defMacroForm.body,
        callSignature: defMacroForm.callSignature
    };
    const frame = context.runtime.environments[0].head;
    util_1.introduceBinding(context, frame, defMacroForm.name.isFromSource, defMacroForm.name.value, macro);
    return sExpression_1.makeEmptyList();
}
//# sourceMappingURL=evaluators.js.map